import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Grid, GridColumn, GridToolbar } from "@progress/kendo-react-grid";
import { Input } from "@progress/kendo-react-inputs";
import _ from "lodash";
import { DateTime } from "luxon";
import * as React from "react";
import { removeSpecialChars } from "../../../classes/StringHelper";
import { IConfig } from "../../../Config";
import DataService from "../../../data/DataService";
import { ConfirmDialog } from "../../Dialogs";
import Loader from "../../Loader";

interface IProps {
    config: IConfig;
    onClose: () => void;
}

interface IState {
    categories: any[];
    categoryName: string;
    editCategoryNameDisabled: boolean;
    existCategoriesNames: string[];
    isAddOpen: boolean;
    isConfirmDeleteOpen: boolean;
    isEditOpen: boolean;
    isLoading: boolean;
    selectedCategoryId: string;
    skip: number;
    take: number;
    totalCategories: number;
}

const CategoriesPerPage = 10;

export default class ManageCategories extends React.Component<IProps, IState> {
    private dataService: DataService;

    public constructor(props) {
        super(props);
        this.state = {
            categories: [],
            categoryName: "",
            editCategoryNameDisabled: false,
            existCategoriesNames: [],
            isAddOpen: false,
            isConfirmDeleteOpen: false,
            isEditOpen: false,
            isLoading: false,
            selectedCategoryId: null,
            skip: 0,
            take: CategoriesPerPage,
            totalCategories: 0,
        };
        this.dataService = new DataService(this.props.config);
    }

    public render() {
        return (
            <Loader isLoading={this.state.isLoading}>
                <div className="CMS_element settings">
                    <div className="CMS_title CMS_T4"><em className="fa fa-tasks" aria-hidden="true"></em> Manage categories</div>
                    <div className="CMS_e_row">
                        <div className="CMS_title CMS_T6">Categories</div>
                        <Grid
                            style={{ height: "400px" }}
                            data={_.map(this.state.categories,
                                (item) => ({
                                    ...item,
                                    createdDate: DateTime.fromISO(item.created).toLocaleString(DateTime.DATETIME_FULL),
                                    selected: item.categoryId === this.state.selectedCategoryId,
                                }))
                            }
                            skip={this.state.skip}
                            take={this.state.take}
                            total={this.state.totalCategories}
                            pageable={true}
                            selectedField="selected"
                            onPageChange={this.onPageChange.bind(this)}
                            onRowClick={(e) => {
                                this.setState({ selectedCategoryId: e.dataItem.categoryId });
                            }}
                        >
                            <GridToolbar>
                                <button
                                    title="Add category"
                                    className="k-button k-primary"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        this.setState({ isAddOpen: true, categoryName: "", editCategoryNameDisabled: false });
                                    }}
                                >
                                    Add category
                                </button>
                                <button
                                    title="Edit category"
                                    className="k-button k-primary"
                                    disabled={_.isEmpty(this.state.selectedCategoryId)}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        const category = _.find(this.state.categories, { categoryId: this.state.selectedCategoryId });
                                        this.setState({ isEditOpen: true, categoryName: category.categoryName });
                                    }}
                                >
                                    Edit category
                                </button>
                                <button
                                    title="Remove category"
                                    className="k-button k-primary"
                                    disabled={_.isEmpty(this.state.selectedCategoryId)}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        this.setState({ isConfirmDeleteOpen: true });
                                    }}
                                >
                                    Remove category
                                </button>
                            </GridToolbar>
                            <GridColumn field="categoryName" title="Category Name" />
                            <GridColumn field="createdDate" title="Created At" />
                        </Grid>
                    </div>
                    <div className="CMS_buttonGroup CMS_bg-R">
                        <button
                            className="CMS_button CMS_bCancel"
                            onClick={(e) => {
                                e.preventDefault();
                                this.props.onClose();
                            }}
                        >
                            Close
                        </button>
                    </div>
                </div>
                <ConfirmDialog
                    isOpen={this.state.isConfirmDeleteOpen}
                    message={`Are you sure you want to delete this category?`}
                    callBack={async (value) => {
                        this.setState({ isConfirmDeleteOpen: false });
                        if (value) {
                            await this.deleteCategory();
                        }
                    }}
                />
                {(this.state.isAddOpen || this.state.isEditOpen)
                    && <Dialog
                        title={`${this.state.isAddOpen ? "Add" : "Edit"} category`}
                        onClose={() => this.setState({ isAddOpen: false, isEditOpen: false })}
                        width={500}
                    >
                        <div style={{ margin: "10px" }}>
                            <Input
                                type="text"
                                placeholder="Category name"
                                autoFocus={true}
                                value={this.state.categoryName}
                                onChange={(e) => this.onChangeCategoryName(e)}
                                style={{ width: "100%" }}
                                maxLength={50}
                            />
                        </div>
                        {this.state.editCategoryNameDisabled &&
                            <div style={{ margin: "10px", color: "red" }}>
                                The Category Name already exists. Plese enter a unique Category Name
                            </div>
                        }
                        <DialogActionsBar>
                            <button
                                className="k-button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    this.setState({ isAddOpen: false, isEditOpen: false, categoryName: "" });
                                }}
                                title="Cancel"
                            >
                                Cancel
                            </button>
                            <button
                                className="k-button"
                                onClick={async (e) => {
                                    e.preventDefault();
                                    if (this.state.isAddOpen) {
                                        await this.addCategory();
                                    } else {
                                        await this.editCategory();
                                    }
                                }}
                                title="OK"
                                disabled={this.state.editCategoryNameDisabled}
                            >
                                OK
                            </button>
                        </DialogActionsBar>
                    </Dialog>}
            </Loader>
        );
    }

    public async componentDidMount() {
        await this.loadCategories();
    }

    private async loadCategories(page: number = 1) {
        try {
            this.setState({ isLoading: true });
            const categories = (await this.dataService.getCategories(page, CategoriesPerPage)).data || {};
            this.setState({ categories: categories.items, totalCategories: categories.total || 0 });
            const allCategories = (await this.dataService.getCategories(1, 1000)).data || {};
            const allNames = this.getDistinctNames(allCategories);
            this.setState({ existCategoriesNames: allNames });
        } finally {
            this.setState({ isLoading: false });
        }
    }

    private getDistinctNames(feeds) {
        if (feeds.items) {
            return feeds.items.map((item) => item.categoryName.toLowerCase())
                .filter((value, index, self) => self.indexOf(value) === index);
        } else {
            return [];
        }
    }

    private OkayButtonDisable(cateogryName) {
        const foundDuplicate = this.state.existCategoriesNames.filter((obj) => obj === cateogryName.toLowerCase());
        this.setState({ editCategoryNameDisabled: foundDuplicate.length > 0 });
    }

    private async onChangeCategoryName(e) {
        const newCategoryName = removeSpecialChars(e.target.value);
        this.setState({ categoryName: newCategoryName });
        this.OkayButtonDisable(newCategoryName);
    }

    private onPageChange(e) {
        this.setState({
            skip: e.page.skip,
            take: e.page.take,
        }, async () => await this.loadCategories(e.page.skip / e.page.take + 1));
    }

    private async addCategory() {
        try {
            this.setState({ isAddOpen: false, isLoading: true });
            await this.dataService.addCategory({ categoryName: this.state.categoryName });
            await this.loadCategories();
        } finally {
            this.setState({ isLoading: false, categoryName: "" });
        }
    }

    private async editCategory() {
        try {
            this.setState({ isEditOpen: false, isLoading: true });
            const category = _.find(this.state.categories, (category) => category.categoryId === this.state.selectedCategoryId);
            category.categoryName = this.state.categoryName;
            await this.dataService.updateCategory(this.state.selectedCategoryId, category);
            await this.loadCategories();
        } finally {
            this.setState({ isLoading: false, categoryName: "" });
        }
    }

    private async deleteCategory() {
        try {
            this.setState({ isLoading: true });
            await this.dataService.deleteCategory(this.state.selectedCategoryId);
            this.setState({ selectedCategoryId: null });
            await this.loadCategories();
        } finally {
            this.setState({ isLoading: false });
        }
    }
};
